import React, { useSyncExternalStore } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { locationToReturnPath } from '../utils/postLoginRedirect';
import { Spinner } from '../components/ui/Spinner';
import {
  hasValidAccessToken,
  readAccessTokenValidity,
  subscribeAccessTokenState,
  getAccessTokenRevision,
} from '../utils/accessTokenState';
import { clearTokens, getRefreshToken } from '../utils/token';
import {
  appendLoginSearchParams,
  getLoginPathForPortal,
  preservePortalForLogout,
  resolvePortalForGuard,
} from '../utils/loginPortal';

interface RequireAuthProps {
  children: React.ReactNode;
  redirectTo?: string;
}

export const RequireAuth: React.FC<RequireAuthProps> = ({
  children,
  redirectTo,
}) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // Re-render when tokens change (logout, expiry clear, devtools, cross-tab).
  useSyncExternalStore(subscribeAccessTokenState, getAccessTokenRevision, () => 0);

  const tokenState = readAccessTokenValidity();
  const hasValidToken = hasValidAccessToken();

  const redirectUnauthenticated = (reason: string) => {
    if (import.meta.env.DEV) {
      console.info(
        '[RequireAuth] redirect',
        `reason=${reason}`,
        `path=${location.pathname}`,
        `token_present=${tokenState.present}`,
        `token_expired=${tokenState.expired}`,
      );
    }
    const returnPath = locationToReturnPath(location.pathname, location.search);
    const portalLogin = getLoginPathForPortal(resolvePortalForGuard(location.pathname), {
      returnTo: returnPath,
    });
    const loginTarget = redirectTo
      ? appendLoginSearchParams(redirectTo, { returnTo: returnPath })
      : portalLogin;
    return <Navigate to={loginTarget} state={{ from: location }} replace />;
  };

  // Access JWT expired with no refresh token → true session end.
  // If refresh exists, stay authenticated; API layer refreshes on demand.
  if (tokenState.present && tokenState.expired && !getRefreshToken()) {
    preservePortalForLogout({ pathname: location.pathname });
    clearTokens();
    return redirectUnauthenticated('expired_token');
  }

  // Fast path — do not wait for AuthContext bootstrap when tokens are missing.
  if (!hasValidToken) {
    return redirectUnauthenticated('missing_token');
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return redirectUnauthenticated('session_not_authenticated');
  }

  return <>{children}</>;
};
